/*
 * Copyright 2018 Darren Lee <balshor@gmail.com>
 *
 * Permission is hereby granted, free of charge, to any person obtaining
 * a copy of this software and associated documentation files (the "Software"),
 * to deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or
 * sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
 * WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR
 * IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
import { IBFRecord } from "./IBFRecord";

const emptyBuffer = () => new DataView(new ArrayBuffer(0));
/*
 * Attempts to read a single IBFRecord from the start of the given buffer.
 *
 * Returns the IBFRecord and a slice of the buffer containing the unread bytes.
 *
 * Returns [null, buffer] if there are insufficient bytes to read another IBFRecord.
 *
 * Throws an Error if the checksum does not match the read data.
 */
export function readIBFRecord(buffer: DataView<ArrayBuffer>): [IBFRecord | undefined, DataView<ArrayBuffer>] {
  const remainingBytes = buffer.byteLength;
  if (remainingBytes < 4) {
    return [undefined, emptyBuffer()];
  }

  const recordSize = buffer.getUint16(0);

  if (remainingBytes < recordSize + 2) {
    return [undefined, emptyBuffer()];
  }

  const checksum = new Int32Array(1);
  const recordData = new DataView<ArrayBuffer>(buffer.buffer.slice(2, recordSize));
  const recordDataArray = new Uint8Array(recordData.buffer);
  for (const value of recordDataArray) {
    checksum[0] += value & 0xff;
  }

  const expectedChecksum = buffer.getUint16(recordSize);

  if (checksum[0] != expectedChecksum) {
    const loggedData = [
      "expectedChecksum = " + expectedChecksum,
      "actualChecksum = " + checksum,
      "recordSize = " + recordSize,
      "bytes = " + recordDataArray.toString(),
    ];
    throw Error("IBFIntegrityException: checksum(" + loggedData.join(", ") + ")");
  }
  const remainingData = new DataView<ArrayBuffer>(buffer.buffer.slice(recordSize + 2));

  return [new IBFRecord(recordData), remainingData];
}
